import type { OnboardingStepProps } from '@rulebooks/evidend-titan';
import { Image } from '@rulebooks/evidend-titan';
import { formatISO, isAfter, isValid, parseISO } from 'date-fns';
import { formatDate } from 'helpers/formatShares';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import { useCookie } from 'react-use';
import { useOrderedReleases } from 'releases/useReleases';

interface IOnboardingContext {
  isShownModal: boolean;
  isShownMessage: boolean;
  steps?: OnboardingStepProps[];
  currentPage: number;
  onPageChange: (page: number) => void;
  onToggleModal: (isShown: boolean) => void;
  onOpenRelease: (index: number) => void;
}

export const OnboardingContext = React.createContext<IOnboardingContext | undefined>({
  isShownModal: false,
  isShownMessage: false,
} as IOnboardingContext);

export const useOnboarding = () => {
  const context = React.useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within a OnboardingProviderProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }: { children: React.ReactNode }) => {
  const [isShownModal, setShowModal] = useState(false);
  const [isShownMessage, setShowMessage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Get last seen date from cookie (might be empty or invalid).
  const [lastSeen, setLastSeen] = useCookie('onboarding');

  // Verify if last seen date is valid.
  const isLastSeenValid = useMemo(() => {
    if (!lastSeen) return false;

    const date = parseISO(lastSeen);
    return isValid(date);
  }, [lastSeen]);

  // Helper function to set the last seen date to now.
  const updateLastSeen = useCallback(() => {
    setLastSeen(formatISO(new Date()));
  }, [setLastSeen]);

  // Automatically set the last seen date to now if it is invalid or missing.
  // This clears invalid values, and sets a value to compare against when the client returns.
  useEffect(() => {
    if (!isLastSeenValid) {
      updateLastSeen();
    }
  }, [isLastSeenValid, updateLastSeen]);

  // Release notes (newest first) based on current locale. The modal always
  // contains all release notes, so the user can navigate through the history.
  const { releases } = useOrderedReleases();

  const steps: OnboardingStepProps[] = useMemo(
    () =>
      releases.map(
        ({ date, title, media, content }) =>
          ({
            title: title ?? '',
            label: formatDate(date),
            content: content ?? '',
            // API media is an image URL (or null). Fall back to an empty
            // element so the required `media` prop is always a ReactElement.
            media: media ? (
              <Image alt={title ?? ''} objectFit="contain" src={media} width="100%" />
            ) : (
              <></>
            ),
          }) as OnboardingStepProps,
      ),
    [releases],
  );

  // There are unread release notes when any has a date after the last seen date.
  const hasNewReleases = useMemo(() => {
    if (!lastSeen || !isLastSeenValid) return false;

    const lastSeenDate = parseISO(lastSeen);
    return releases.some(({ date }) => date && isAfter(date, lastSeenDate));
  }, [releases, isLastSeenValid, lastSeen]);

  // Helper to toggle the onboarding modal, and reset the last seen date when it is closed.
  const onToggleModal = useCallback(
    (isShown: boolean) => {
      setShowModal(isShown);

      // When opening via the message, start at the newest release note.
      if (isShown) {
        setCurrentPage(1);
      }

      // When closing the modal, set the cookie to the current date and hide the message
      if (!isShown) {
        setLastSeen(formatISO(new Date()));
        setShowMessage(false);
      }
    },
    [setLastSeen],
  );

  // Open the modal at a specific release note (index in the ordered releases).
  const onOpenRelease = useCallback((index: number) => {
    setCurrentPage(index + 1);
    setShowModal(true);
  }, []);

  // Start showing the onboarding message if there are new release notes.
  useEffect(() => {
    if (!hasNewReleases) return;

    setShowMessage(true);
  }, [hasNewReleases]);

  const context = useMemo(
    () => ({
      isShownModal,
      isShownMessage,
      steps,
      currentPage,
      onPageChange: setCurrentPage,
      onToggleModal,
      onOpenRelease,
    }),
    [isShownModal, isShownMessage, steps, currentPage, onToggleModal, onOpenRelease],
  );

  return <OnboardingContext.Provider value={context}>{children}</OnboardingContext.Provider>;
};
